/**
 * Book a Demo Page – /book-a-demo
 * Public landing page for Seatrade Cruise / Lufthansa Industry Booth demo bookings.
 */
import { SEO } from "@/components/SEO";
import apiClient from "@/services/api/apiClient";
import {
  Building2,
  CalendarCheck,
  CheckCircle2,
  Clock,
  Mail,
  MapPin,
  User,
  Users,
} from "lucide-react";
import React, { useState } from "react";

const PREFERRED_TIMES = [
  "Tue 14 Apr · 10:00 – 11:00",
  "Tue 14 Apr · 11:00 – 12:00",
  "Tue 14 Apr · 12:00 – 13:00",
  "Tue 14 Apr · 13:00 – 14:00",
  "Tue 14 Apr · 14:00 – 15:00",
  "Tue 14 Apr · 15:00 – 16:00",
  "Wed 15 Apr · 10:00 – 11:00",
  "Wed 15 Apr · 11:00 – 12:00",
  "Wed 15 Apr · 12:00 – 13:00",
  "Wed 15 Apr · 13:00 – 14:00",
  "Wed 15 Apr · 14:00 – 15:00",
  "Wed 15 Apr · 15:00 – 16:00",
  "Thu 16 Apr · 10:00 – 11:00",
  "Thu 16 Apr · 11:00 – 12:00",
  "Thu 16 Apr · 12:00 – 13:00",
  "Thu 16 Apr · 13:00 – 14:00",
  "Thu 16 Apr · 14:00 – 15:00",
  "Thu 16 Apr · 15:00 – 16:00",
];

interface FormState {
  name: string;
  organisation: string;
  role: string;
  email: string;
  preferredTime: string;
  location: string;
  message: string;
}

const INITIAL_FORM: FormState = {
  name: "",
  organisation: "",
  role: "",
  email: "",
  preferredTime: "",
  location: "Lufthansa Industry Booth at Seatrade",
  message: "",
};

export const BookADemoPage: React.FC = () => {
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<
    Partial<Record<keyof FormState, string>>
  >({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState("");

  const set =
    (field: keyof FormState) =>
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
      setErrors((prev) => ({ ...prev, [field]: "" }));
    };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.organisation.trim())
      newErrors.organisation = "Organisation is required";
    if (!form.role.trim()) newErrors.role = "Role is required";
    if (!form.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      newErrors.email = "Please enter a valid email address";
    if (!form.preferredTime) newErrors.preferredTime = "Please select a time";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setServerError("");
    try {
      await apiClient.post("/demo/book", form);
      setSubmitted(true);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ||
        "Something went wrong. Please try again or email us directly at mh@innovizr.team";
      setServerError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Book a Demo – HEALTHPASS at Seatrade Cruise"
        description="Schedule a live HEALTHPASS demo at the Lufthansa Industry Booth at Seatrade Cruise. See how we transform passenger health management for the cruise industry."
      />

      <div className="min-h-screen bg-gray-50">
        {/* Hero */}
        <div
          className="text-white py-16 px-4"
          style={{
            background:
              "linear-gradient(135deg, #1d4ed8 0%, #1e40af 50%, #7c3aed 100%)",
          }}
        >
          <div className="max-w-3xl mx-auto text-center">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img
                src="/images/healthpass_logo.svg"
                alt="HEALTHPASS"
                className="h-10 w-auto brightness-0 invert"
              />
            </div>
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur rounded-full px-4 py-1.5 text-sm font-medium mb-6">
              <MapPin className="w-4 h-4" />
              Seatrade Cruise – Lufthansa Industry Booth
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-4 leading-tight">
              Book a Live HEALTHPASS Demo
            </h1>
            <p className="text-blue-100 text-lg max-w-xl mx-auto">
              Discover how HEALTHPASS can transform health management and
              documentation for your passengers, crew, and operations.
            </p>
          </div>
        </div>

        {/* Feature pills */}
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-3xl mx-auto px-4 py-4 flex flex-wrap gap-3 justify-center">
            {[
              { icon: Clock, text: "15-minute live demo" },
              { icon: Users, text: "Tailored to your role" },
              { icon: CalendarCheck, text: "Book your time slot" },
            ].map(({ icon: Icon, text }) => (
              <div
                key={text}
                className="flex items-center gap-1.5 text-sm text-gray-600 bg-gray-50 rounded-full px-3 py-1.5"
              >
                <Icon className="w-3.5 h-3.5 text-blue-500" />
                {text}
              </div>
            ))}
          </div>
        </div>

        {/* Form / Success */}
        <div className="max-w-2xl mx-auto px-4 py-12">
          {submitted ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10 text-center">
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Demo Request Received!
              </h2>
              <p className="text-gray-500 max-w-sm mx-auto mb-6">
                Thank you, <strong>{form.name}</strong>. We'll confirm your time
                slot at <strong>{form.preferredTime}</strong> and send a
                calendar invite to <strong>{form.email}</strong> shortly.
              </p>
              <button
                onClick={() => {
                  setForm(INITIAL_FORM);
                  setSubmitted(false);
                }}
                className="text-sm text-blue-600 hover:underline"
              >
                Submit another request
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
              <h2 className="text-lg font-bold text-gray-900 mb-1">
                Your Contact Details
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                All fields marked <span className="text-red-500">*</span> are
                required.
              </p>

              <form onSubmit={handleSubmit} noValidate className="space-y-5">
                {/* Name */}
                <Field
                  id="name"
                  label="Full Name"
                  icon={User}
                  error={errors.name}
                >
                  <input
                    id="name"
                    type="text"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Jane Smith"
                    autoComplete="name"
                    className={inputCls(!!errors.name)}
                  />
                </Field>

                {/* Organisation */}
                <Field
                  id="organisation"
                  label="Organisation"
                  icon={Building2}
                  error={errors.organisation}
                >
                  <input
                    id="organisation"
                    type="text"
                    value={form.organisation}
                    onChange={set("organisation")}
                    placeholder="Cruise Line International"
                    autoComplete="organization"
                    className={inputCls(!!errors.organisation)}
                  />
                </Field>

                {/* Role */}
                <Field
                  id="role"
                  label="Role / Job Title"
                  icon={Users}
                  error={errors.role}
                >
                  <input
                    id="role"
                    type="text"
                    value={form.role}
                    onChange={set("role")}
                    placeholder="Head of Passenger Operations"
                    autoComplete="organization-title"
                    className={inputCls(!!errors.role)}
                  />
                </Field>

                {/* Email */}
                <Field
                  id="email"
                  label="Email Address"
                  icon={Mail}
                  error={errors.email}
                >
                  <input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="jane@yourcompany.com"
                    autoComplete="email"
                    className={inputCls(!!errors.email)}
                  />
                </Field>

                {/* Preferred Time */}
                <Field
                  id="preferredTime"
                  label="Preferred Demo Time"
                  icon={Clock}
                  error={errors.preferredTime}
                >
                  <select
                    id="preferredTime"
                    value={form.preferredTime}
                    onChange={set("preferredTime")}
                    className={inputCls(!!errors.preferredTime)}
                  >
                    <option value="">— Select a time slot —</option>
                    {PREFERRED_TIMES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </Field>

                {/* Location (read-only) */}
                <Field id="location" label="Location" icon={MapPin}>
                  <input
                    id="location"
                    type="text"
                    value={form.location}
                    readOnly
                    className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-600 text-sm cursor-default"
                  />
                </Field>

                {/* Optional message */}
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Additional Information{" "}
                    <span className="text-gray-400 font-normal">
                      (optional)
                    </span>
                  </label>
                  <textarea
                    id="message"
                    rows={3}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Anything you'd like us to know or prepare for the demo…"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>

                {serverError && (
                  <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3">
                    {serverError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <CalendarCheck className="w-4 h-4" />
                      Book My Demo Slot
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-400 text-center">
                  Your data is used solely to schedule the demo and will not be
                  shared with third parties. HEALTHPASS · travelhealthpass.app
                </p>
              </form>
            </div>
          )}
        </div>

        {/* Footer strip */}
        <div className="text-center pb-10">
          <a
            href="/"
            className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            ← Back to travelhealthpass.app
          </a>
        </div>
      </div>
    </>
  );
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function inputCls(hasError: boolean): string {
  return [
    "w-full px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
    hasError ? "border-red-400 bg-red-50" : "border-gray-300 bg-white",
  ].join(" ");
}

interface FieldProps {
  id: string;
  label: string;
  icon: React.FC<{ className?: string }>;
  error?: string;
  children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ id, label, error, children }) => (
  <div>
    <label
      htmlFor={id}
      className="block text-sm font-medium text-gray-700 mb-1"
    >
      {label} <span className="text-red-500">*</span>
    </label>
    {children}
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);
